import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAvatarForUser } from '@/utils/avatar';
import {
  useAuthStore,
  useSystemNotificationStore,
  SystemNotificationType,
  MERCHANT_ROUTES,
  ToastContainer,
  apiClient,
  API_ENDPOINTS,
  Order,
  OrderStatus,
  useUpgradeStore,
  SALES_CHANNEL_LIMITS,
  PLAN_LIMITS,
} from '@dukene/shared';
import {
  SquaresFour,
  Package,
  ShoppingCart,
  Users,
  ChartBar,
  GearSix,
  Storefront,
  CaretDown,
  Bell,
  MagnifyingGlass,
  List,
  SignOut,
  Tag,
  Truck,
  ShareNetwork,
  Plus,
  CaretLeft,
  CaretRight,
  GoogleLogo as PhosphorGoogleLogo,
  Link as PhosphorLink,
  WarningCircle,
  Info,
  CreditCard,
  Trash,
  Eye,
  Code,
  ChatCircle,
  FacebookLogo as PhosphorFacebookLogo,
} from '@phosphor-icons/react';
import { SSEProvider } from '../../contexts/SSEContext';
import UpgradeModal from '../modals/UpgradeModal';
import AddSalesChannelModal, { SalesChannelOption } from '../modals/AddSalesChannelModal';

// --- Types ---

interface NavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: number;
  children?: {
    label: string;
    href: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
}

// --- Sub-components ---

interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  navigation: NavItem[];
  user: any;
  handleLogout: () => void;
  expandedItems: string[];
  toggleExpanded: (label: string) => void;
  isActive: (href: string) => boolean;
  isChildActive: (children?: { href: string }[]) => boolean;
  storeName?: string;
  store?: any;
  hasOnlineStoreChannel?: boolean;
  salesChannels: NavItem[];
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  ownedStores?: any[];
  onStoreSwitch: (storeId: string) => void;
  activeStoreId: string | null;
  navigate: (href: string) => void;
  location: any;
  onOpenAddChannelModal: () => void;
}

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  navigation,
  user,
  handleLogout,
  expandedItems,
  toggleExpanded,
  isActive,
  isChildActive,
  storeName,
  store,
  hasOnlineStoreChannel = false,
  salesChannels,
  isCollapsed,
  setIsCollapsed,
  ownedStores = [],
  onStoreSwitch,
  activeStoreId,
  navigate,
  location,
  onOpenAddChannelModal,
}: SidebarProps) => {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const currentPlan = user?.plan || 'BASIC';
  const storeLimit = (PLAN_LIMITS as any)[currentPlan] ?? 1;
  const canCreateMore = ownedStores.length < storeLimit;
  const hasMultipleStores = ownedStores.length > 1;

  const handleCreateStoreClick = () => {
    setSwitcherOpen(false);
    setSidebarOpen(false);
    if (!canCreateMore) {
      useUpgradeStore.getState().open('store_limit');
      return;
    }
    navigate(MERCHANT_ROUTES.SETTINGS_STORES_NEW);
  };

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 ${isCollapsed ? 'lg:w-[90px]' : 'lg:w-64'} w-64 bg-[#F1F1F1] border-r border-[#D2D5D9] transform transition-all duration-300 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } flex flex-col font-sans`}
    >
      {/* Floating Expand/Collapse Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="hidden lg:flex absolute top-6 -right-4 w-8 h-8 bg-white border border-[#D2D5D9] rounded-full shadow-sm items-center justify-center text-[#5C5F62] hover:text-[#202223] hover:bg-gray-50 transition-all z-[60]"
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {isCollapsed ? <CaretRight weight="bold" className="w-4 h-4" /> : <CaretLeft weight="bold" className="w-4 h-4" />}
      </button>

      {/* Store Switcher & Logo Section */}
      <div className={`p-3 border-b border-[#D2D5D9] bg-white/50 backdrop-blur-sm flex ${isCollapsed ? 'flex-col items-center gap-3' : 'items-center justify-between gap-2'}`}>
        <div className="relative flex-1">
          <button
            onClick={() => setSwitcherOpen(!switcherOpen)}
            className={`flex items-center gap-3 p-2 rounded-lg transition-all group border border-transparent w-full ${isCollapsed ? 'justify-center p-0' : 'text-left min-w-0'} hover:bg-[#EBEBEB] hover:border-[#D2D5D9]`}
            title={isCollapsed ? storeName || 'DUKENE' : undefined}
          >
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform flex-shrink-0 overflow-hidden p-0.5 border border-[#D2D5D9]">
              <img src="/favicon.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            {!isCollapsed && (
              <div className="flex-1 text-left min-w-0 overflow-hidden animate-in fade-in slide-in-from-left-2 duration-300">
                <h2 className="text-[13px] font-bold text-[#202223] truncate leading-tight uppercase tracking-tight">
                  {storeName || 'DUKENE'}
                </h2>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <p className="text-[10px] text-[#5C5F62] font-semibold flex items-center gap-1">
                    <span>{hasMultipleStores ? `${ownedStores.length} Stores` : 'Active Store'}</span>
                    <CaretDown className={`w-3 h-3 transition-transform ${switcherOpen ? 'rotate-180' : ''}`} weight="bold" />
                  </p>
                </div>
              </div>
            )}
          </button>

          {switcherOpen && !isCollapsed && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#D2D5D9] rounded-lg shadow-xl z-[60] py-1 animate-in fade-in zoom-in-95 duration-100">
              <div className="px-3 py-1.5 border-b border-[#D2D5D9] flex items-center justify-between">
                <span className="text-[11px] font-bold text-[#5C5F62] uppercase tracking-wider">Your Stores</span>
                <span className="text-[10px] bg-emerald/10 text-emerald px-1.5 py-0.5 rounded font-bold">
                  {ownedStores.length} / {storeLimit === 999 ? '∞' : storeLimit}
                </span>
              </div>
              <div className="max-h-[220px] overflow-y-auto overflow-x-hidden custom-scrollbar divide-y divide-gray-100">
                {ownedStores.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => {
                      onStoreSwitch(s.id);
                      setSwitcherOpen(false);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#F6F6F7] transition-colors text-left ${
                      activeStoreId === s.id ? 'bg-[#F6F6F7] border-l-4 border-emerald' : ''
                    }`}
                  >
                    <div className="w-8 h-8 bg-[#F1F1F1] rounded flex items-center justify-center text-[#5C5F62] shrink-0">
                      <Storefront weight="fill" className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-1">
                        <p className={`text-[13px] truncate ${activeStoreId === s.id ? 'font-bold text-[#202223]' : 'text-[#5C5F62]'}`}>
                          {s.name}
                        </p>
                        {activeStoreId === s.id && (
                          <span className="text-[9px] font-bold uppercase tracking-wider text-emerald px-1.5 py-0.2 rounded bg-emerald/10 shrink-0">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-[10px] text-[#919191] truncate">
                        {s.subdomain}.dukene.com
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              <div className="px-1 pt-1 border-t border-[#D2D5D9]">
                <button 
                  onClick={handleCreateStoreClick}
                  className="w-full flex items-center justify-between px-3 py-2 text-[12px] font-semibold text-emerald hover:bg-emerald/5 rounded-md transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Plus weight="bold" className="w-4 h-4" />
                    Create New Store
                  </span>
                  {!canCreateMore && (
                    <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                      Upgrade
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 custom-scrollbar">
        {/* Navigation - Shopify Polaris Compact Hierarchy */}
        <nav className="px-2.5 space-y-0.5">
          {navigation.map((item) => {
            const Icon = item.icon;
            const hasChildren = item.children && item.children.length > 0;
            const isExpanded = expandedItems.includes(item.label);
            const active = isActive(item.href) || isChildActive(item.children);

            return (
              <div key={item.label}>
                {hasChildren ? (
                  <>
                    <button
                      onClick={() => {
                        if (isCollapsed) {
                          navigate(item.href);
                          setSidebarOpen(false);
                          return;
                        }
                        toggleExpanded(item.label);
                      }}
                      className={`w-full flex rounded-md transition-colors ${
                        active
                          ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
                          : 'text-[#202223] hover:bg-[#EBEBEB]'
                      } ${isCollapsed ? 'flex-col items-center justify-center py-1.5 px-1 gap-1' : 'items-center justify-between py-1 px-2.5 text-[13px] leading-5'}`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <div className={`flex min-w-0 ${isCollapsed ? 'flex-col items-center gap-1.5 w-full' : 'items-center gap-2.5'}`}>
                        <Icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-[#5C5F62] flex-shrink-0`} />
                        <span className={isCollapsed ? 'text-[9px] font-medium text-center w-full leading-[1.1] break-words' : 'truncate'}>{item.label}</span>
                      </div>
                      {!isCollapsed && (
                        <div className="flex items-center gap-1.5">
                          {item.badge && item.badge > 0 && (
                            <span className="px-1.5 py-0.5 bg-[#E1E4E8] text-[#202223] text-[11px] font-medium rounded-full">
                              {item.badge}
                            </span>
                          )}
                          <CaretDown
                            weight="bold"
                            className={`w-3.5 h-3.5 text-[#5C5F62] transition-transform duration-200 ${
                              isExpanded ? '' : '-rotate-90'
                            }`}
                          />
                        </div>
                      )}
                    </button>

                    {!isCollapsed && isExpanded && item.children && (
                      <div className="pl-9 space-y-0.5 pt-0.5 animate-in slide-in-from-top-1 duration-150">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`block py-1 px-2 rounded text-[12px] leading-4 transition-colors ${
                              isActive(child.href)
                                ? 'text-[#202223] font-bold bg-[#E1E4E8]/60'
                                : 'text-[#5C5F62] hover:text-[#202223] hover:bg-[#EBEBEB]'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            <span className="truncate">{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.href}
                    className={`flex rounded-md transition-colors ${
                      active
                        ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
                        : 'text-[#202223] hover:bg-[#EBEBEB]'
                    } ${isCollapsed ? 'flex-col items-center justify-center py-1.5 px-1 gap-1' : 'items-center justify-between py-1 px-2.5 text-[13px] leading-5'}`}
                    onClick={() => setSidebarOpen(false)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <div className={`flex min-w-0 ${isCollapsed ? 'flex-col items-center gap-1.5 w-full' : 'items-center gap-2.5'}`}>
                      <Icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-[#5C5F62] flex-shrink-0`} />
                      <span className={isCollapsed ? 'text-[9px] font-medium text-center w-full leading-[1.1] break-words' : 'truncate'}>{item.label}</span>
                    </div>
                    {!isCollapsed && item.badge && item.badge > 0 && (
                      <span className="px-1.5 py-0.5 bg-[#E1E4E8] text-[#202223] text-[11px] font-medium rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sales Channels - Compact section matching Shopify Polaris */}
        <div className="mt-3 pt-3 border-t border-[#D2D5D9]/60">
          <div className={`px-4 py-1.5 flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            <span className={`text-[11px] font-bold text-[#5C5F62] uppercase tracking-wider ${isCollapsed ? 'hidden' : 'block'}`}>
              Sales Channels
            </span>
            <button
              onClick={onOpenAddChannelModal}
              className="p-1 rounded hover:bg-[#E1E4E8] text-[#5C5F62] hover:text-[#202223] transition-colors"
              title="Add sales channel"
            >
              <Plus weight="bold" className="w-3.5 h-3.5" />
            </button>
          </div>

          <nav className="px-2.5 space-y-0.5 mt-0.5">
            {/* Online Store with view storefront eye icon */}
            {hasOnlineStoreChannel && (
              <div className="group relative flex items-center">
                <Link
                  to="/store"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex-1 flex rounded-md transition-colors ${
                    location.pathname.startsWith('/store')
                      ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
                      : 'text-[#202223] hover:bg-[#EBEBEB]'
                  } ${isCollapsed ? 'flex-col items-center justify-center py-1.5 px-1 gap-1 w-full' : 'items-center gap-2.5 py-1 px-2.5 text-[13px] leading-5'}`}
                  title={isCollapsed ? 'Online Store' : undefined}
                >
                  <Storefront weight="fill" className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-[#5C5F62] flex-shrink-0`} />
                  <span className={isCollapsed ? 'text-[9px] font-medium text-center w-full leading-[1.1] break-words' : 'truncate'}>Online Store</span>
                </Link>
                {!isCollapsed && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (store?.status === 'ACTIVE') {
                        const subdomain = store?.subdomain || 'shop';
                        const host = window.location.host.includes('localhost')
                          ? `${subdomain}.localhost:5173`
                          : `${subdomain}.dukene.com`;
                        const protocol = window.location.protocol;
                        window.open(`${protocol}//${host}`, '_blank');
                      } else {
                        navigate('/store');
                      }
                    }}
                    className="absolute right-2 p-1 rounded hover:bg-[#D4D7DB] text-[#5C5F62] hover:text-[#202223] transition-colors"
                    title={store?.status === 'ACTIVE' ? "View Live Storefront" : "Store Offline - Publish Now"}
                  >
                    <Eye weight="fill" className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}

            {/* Connected Sales Channels */}
            {salesChannels.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex rounded-md transition-colors ${
                    active
                      ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
                      : 'text-[#202223] hover:bg-[#EBEBEB]'
                  } ${isCollapsed ? 'flex-col items-center justify-center py-1.5 px-1 gap-1 w-full' : 'items-center gap-2.5 py-1 px-2.5 text-[13px] leading-5'}`}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-[#5C5F62] flex-shrink-0`} />
                  <span className={isCollapsed ? 'text-[9px] font-medium text-center w-full leading-[1.1] break-words' : 'truncate'}>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Sidebar Footer - Settings & User Account */}
      <div className="p-2.5 border-t border-[#D2D5D9] bg-[#F1F1F1] space-y-1">
        <Link
          to={MERCHANT_ROUTES.SETTINGS}
          onClick={() => setSidebarOpen(false)}
          className={`flex rounded-md transition-colors ${
            location.pathname.startsWith(MERCHANT_ROUTES.SETTINGS)
              ? 'bg-[#E1E4E8] text-[#202223] font-semibold'
              : 'text-[#202223] hover:bg-[#EBEBEB]'
          } ${isCollapsed ? 'flex-col items-center justify-center py-1.5 px-1 gap-1 w-full' : 'items-center gap-2.5 py-1 px-2.5 text-[13px] leading-5'}`}
          title={isCollapsed ? 'Settings' : undefined}
        >
          <GearSix weight="fill" className={`${isCollapsed ? 'w-5 h-5' : 'w-4 h-4'} text-[#5C5F62] flex-shrink-0`} />
          <span className={isCollapsed ? 'text-[9px] font-medium text-center w-full leading-[1.1] break-words' : 'truncate'}>Settings</span>
        </Link>

        {isCollapsed ? (
          <div className="flex flex-col items-center gap-3 pt-1">
            <div
              className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 cursor-default overflow-hidden bg-emerald/10"
              title={user?.firstName || 'Merchant'}
            >
              <img src={getAvatarForUser(user?.id || user?.email)} alt="Avatar" className="w-full h-full object-cover" />
            </div>
            <button onClick={handleLogout} className="text-[#5C5F62] hover:text-red-600 transition-colors p-1.5" title="Logout">
              <SignOut weight="fill" className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="pt-1 px-2.5 py-1 flex items-center justify-between">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="w-7 h-7 rounded flex items-center justify-center flex-shrink-0 overflow-hidden bg-emerald/10">
                <img src={getAvatarForUser(user?.id || user?.email)} alt="Avatar" className="w-full h-full object-cover" />
              </div>
              <span className="text-[13px] font-medium truncate text-[#202223]">{user?.firstName || 'Merchant'}</span>
            </div>
            <button onClick={handleLogout} className="text-[#5C5F62] hover:text-red-600 transition-colors p-1" title="Logout">
              <SignOut weight="fill" className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

const notifIconMap: Record<SystemNotificationType, React.ElementType> = {
  ORDER: (props: any) => <Package weight="fill" {...props} />,
  INVENTORY: (props: any) => <WarningCircle weight="fill" {...props} />,
  SYSTEM: (props: any) => <Info weight="fill" {...props} />,
  PAYMENT: (props: any) => <CreditCard weight="fill" {...props} />,
};

const notifColorMap: Record<SystemNotificationType, string> = {
  ORDER: 'text-[#2C6ECB] bg-[#EBF5FF] dark:bg-blue-950/40 dark:text-blue-400',
  INVENTORY: 'text-[#B98900] bg-[#FFF5EA] dark:bg-amber-950/40 dark:text-amber-400',
  SYSTEM: 'text-[#5C5F62] bg-[#F6F6F7] dark:bg-slate-800 dark:text-slate-300',
  PAYMENT: 'text-[#006838] bg-[#E3F1DF] dark:bg-emerald-950/40 dark:text-emerald-400',
};

interface HeaderProps {
  setSidebarOpen: (open: boolean) => void;
  notificationsOpen: boolean;
  setNotificationsOpen: (open: boolean) => void;
  unreadCount: number;
  user: any;
  navigate: (path: string) => void;
}

const Header = ({
  setSidebarOpen,
  notificationsOpen,
  setNotificationsOpen,
  unreadCount,
  user,
  navigate,
}: HeaderProps) => {
  const { notifications, fetchNotifications, markAsRead, markAllAsRead, removeNotification } = useSystemNotificationStore();

  return (
    <header className="h-[48px] bg-white dark:bg-[#1A1D21] border-b border-[#D2D5D9] dark:border-slate-800 flex items-center justify-between px-4 lg:px-8 flex-shrink-0 z-40 sticky top-0">
      <div className="flex items-center gap-4 flex-1">
        <button
          className="lg:hidden p-2 text-[#5C5F62] dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-md transition-colors"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open navigation menu"
        >
          <List weight="bold" className="w-5 h-5" />
        </button>

        <div className="relative w-full max-w-xl hidden md:block">
          <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5F62] dark:text-slate-400" />
          <input
            type="search"
            placeholder="Search products, orders, customers..."
            className="w-full h-8 pl-10 pr-4 rounded-lg bg-[#f1f1f1] dark:bg-slate-800/80 border border-[#d1d1d1] dark:border-slate-700 text-sm text-[#303030] dark:text-white placeholder-[#5C5F62] dark:placeholder-slate-400 focus:outline-none focus:bg-white dark:focus:bg-[#1A1D21] focus:ring-1 focus:ring-emerald transition-all"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative">
          <button
            className={`p-2 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors relative group ${notificationsOpen ? 'bg-gray-100 dark:bg-slate-800' : ''}`}
            onClick={() => {
              if (!notificationsOpen) fetchNotifications();
              setNotificationsOpen(!notificationsOpen);
            }}
            aria-label="Toggle notifications"
          >
            <Bell weight="fill" className="w-5 h-5 text-[#5C5F62] dark:text-slate-400 group-hover:text-emerald transition-colors" />
            {unreadCount > 0 && (
              <span className="absolute top-2.5 right-2.5 h-2 w-2 bg-[#DE3618] rounded-full ring-2 ring-white dark:ring-[#1A1D21]"></span>
            )}
          </button>

          {notificationsOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setNotificationsOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white dark:bg-[#1A1D21] rounded-2xl shadow-2xl border border-[#D2D5D9] dark:border-slate-800 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                <div className="p-4 bg-gray-50/80 dark:bg-[#141618] border-b border-[#D2D5D9] dark:border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Notifications</h3>
                    {unreadCount > 0 && (
                      <span className="px-2 py-0.5 bg-emerald/10 text-emerald text-[11px] font-bold rounded-full">
                        {unreadCount} new
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={() => markAllAsRead()}
                      className="text-xs font-medium text-emerald hover:underline flex items-center gap-1"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-[380px] overflow-y-auto divide-y divide-[#E1E3E5] dark:divide-slate-800">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-[#5C5F62] dark:text-slate-400 space-y-2">
                      <Bell weight="duotone" className="w-8 h-8 mx-auto opacity-40 text-[#5C5F62] dark:text-slate-400" />
                      <p className="text-xs font-medium">No notifications yet</p>
                    </div>
                  ) : (
                    notifications
                      .filter((n) => n && n.id && n.type)
                      .slice(0, 5)
                      .map((notification) => {
                        const IconComponent = notifIconMap[notification.type] || Info;
                        const colorClass = notifColorMap[notification.type] || 'text-[#5C5F62] bg-[#F6F6F7] dark:bg-slate-800 dark:text-slate-300';
                        let dateStr = 'Recent';
                        try {
                          if (notification.createdAt) {
                            const d = new Date(notification.createdAt);
                            if (!isNaN(d.getTime())) {
                              dateStr = format(d, 'MMM d');
                            }
                          }
                        } catch (e) {}
                        return (
                          <div
                            key={notification.id}
                            onClick={() => {
                              if (!notification.read) markAsRead(notification.id);
                              if (notification.link) {
                                navigate(notification.link);
                              } else {
                                navigate(MERCHANT_ROUTES.NOTIFICATIONS);
                              }
                              setNotificationsOpen(false);
                            }}
                            className={`p-4 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors cursor-pointer flex gap-3 relative group ${
                              !notification.read ? 'bg-emerald/[0.03] dark:bg-emerald/5' : ''
                            }`}
                          >
                            {!notification.read && (
                              <span className="absolute left-1.5 top-5 w-1.5 h-1.5 rounded-full bg-emerald" />
                            )}
                            <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 ${colorClass}`}>
                              <IconComponent className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <p className={`text-xs font-semibold truncate ${!notification.read ? 'text-[#202223] dark:text-white' : 'text-[#303030] dark:text-slate-300'}`}>
                                  {notification.title}
                                </p>
                                <span className="text-[10px] text-[#8C9196] dark:text-slate-500 shrink-0">
                                  {dateStr}
                                </span>
                              </div>
                              <p className="text-xs text-[#5C5F62] dark:text-slate-400 line-clamp-2 mt-0.5 leading-relaxed">
                                {notification.message}
                              </p>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeNotification(notification.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 text-[#5C5F62] dark:text-slate-400 hover:text-red-500 transition-opacity self-start"
                              aria-label="Delete notification"
                            >
                              <Trash weight="fill" className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      })
                  )}
                </div>

                <div className="p-3 bg-gray-50/80 dark:bg-[#141618] border-t border-[#D2D5D9] dark:border-slate-800 text-center">
                  <button
                    onClick={() => {
                      setNotificationsOpen(false);
                      navigate(MERCHANT_ROUTES.NOTIFICATIONS);
                    }}
                    className="text-xs font-semibold text-emerald hover:text-emerald/80 transition-colors w-full py-1"
                  >
                    View All Notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="w-px h-6 bg-[#D2D5D9] mx-1" />

        <button
          onClick={() => navigate(MERCHANT_ROUTES.PROFILE)}
          className="flex items-center gap-2.5 pl-1 group"
        >
          <div className="w-8 h-8 rounded-lg overflow-hidden bg-emerald/10 shadow-sm transition-transform group-hover:scale-105 active:scale-95">
            <img src={getAvatarForUser(user?.id || user?.email)} alt="Avatar" className="w-full h-full object-cover" />
          </div>
        </button>
      </div>
    </header>
  );
};

// --- Main Layout ---

export default function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, activeStoreId, setActiveStore } = useAuthStore();
  const { unreadCount, fetchNotifications } = useSystemNotificationStore();


  useEffect(() => {
    if (activeStoreId) {
      fetchNotifications();
    }
  }, [fetchNotifications, activeStoreId]);

  // SSE connection is now managed by SSEProvider

  const { data: orders = [] } = useQuery<Order[]>({
    queryKey: ['merchant-orders', activeStoreId],
    queryFn: async () => {
      const response = await apiClient.get<Order[] | { orders?: Order[]; data?: Order[] }>(
        API_ENDPOINTS.ORDERS.LIST
      );
      if (Array.isArray(response)) return response;
      return response?.orders || response?.data || [];
    },
    refetchInterval: 30000,
    enabled: !!activeStoreId,
  });

  const orderCount = orders.filter(order => {
    const paymentStatus = String(
      order.paymentDetails?.status || order.paymentStatus || ''
    ).toUpperCase();
    const fulfillmentStatus = String(order.fulfillmentStatus || '').toUpperCase();
    const isPaid = ['PAID', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(paymentStatus);
    const isAwaitingFulfillment =
      fulfillmentStatus !== 'FULFILLED' &&
      fulfillmentStatus !== 'CANCELLED' &&
      order.status !== OrderStatus.SHIPPED &&
      order.status !== OrderStatus.DELIVERED &&
      order.status !== OrderStatus.CANCELLED &&
      order.status !== OrderStatus.REFUNDED;

    return isPaid && isAwaitingFulfillment;
  }).length;

  const { data: store } = useQuery<any>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: async () => apiClient.get<any>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });

  const { data: storesResponse } = useQuery<any[]>({
    queryKey: ['merchant-stores'],
    queryFn: async () => apiClient.get<any[]>('/stores/merchant/stores'),
    enabled: !!user,
  });

  useEffect(() => {
    if (storesResponse && Array.isArray(storesResponse) && storesResponse.length > 0) {
      const activeStoreIsAuthorized = storesResponse.some((candidate) => candidate?.id === activeStoreId);
      if (activeStoreId && activeStoreIsAuthorized) return;

      const firstStore = storesResponse[0];
      if (firstStore?.id) {
        setActiveStore(firstStore.id);
      }
    }
  }, [activeStoreId, storesResponse, setActiveStore]);

  const handleStoreSwitch = (storeId: string) => {
    setActiveStore(storeId);
    navigate(MERCHANT_ROUTES.DASHBOARD, { replace: true });
  };


  // --- Final Grouped Navigation Structure ---
  const mainNavigation: NavItem[] = useMemo(() => [
    {
      label: 'Dashboard',
      icon: (props: any) => <SquaresFour weight="fill" {...props} />,
      href: MERCHANT_ROUTES.DASHBOARD,
    },
    {
      label: 'Products',
      icon: (props: any) => <Package weight="fill" {...props} />,
      href: MERCHANT_ROUTES.PRODUCTS,
      children: [
        { label: 'All Products', href: MERCHANT_ROUTES.PRODUCTS },
        { label: 'Categories', href: MERCHANT_ROUTES.CATEGORIES },
        { label: 'Inventory', href: MERCHANT_ROUTES.INVENTORY },
        { label: 'Reviews', href: '/products/reviews' },
      ],
    },
    {
      label: 'Orders',
      icon: (props: any) => <ShoppingCart weight="fill" {...props} />,
      href: MERCHANT_ROUTES.ORDERS,
      badge: orderCount,
      children: [
        { label: 'All Orders', href: MERCHANT_ROUTES.ORDERS },
        { label: 'Order Routes', href: '/shipping/routes' },
      ],
    },
    {
      label: 'Customers',
      icon: (props: any) => <Users weight="fill" {...props} />,
      href: MERCHANT_ROUTES.CUSTOMERS,
    },
    {
      label: 'Suppliers',
      icon: (props: any) => <Truck weight="fill" {...props} />,
      href: '/suppliers',
      children: [
        { label: 'My Suppliers', href: '/suppliers' },
        { label: 'Marketplace', href: '/suppliers/marketplace' },
      ],
    },
    {
      label: 'Discounts',
      icon: (props: any) => <Tag weight="fill" {...props} />,
      href: '/discounts',
    },
    {
      label: 'Analytics',
      icon: (props: any) => <ChartBar weight="fill" {...props} />,
      href: MERCHANT_ROUTES.ANALYTICS,
    },
    {
      label: 'Payment Links',
      icon: (props: any) => <PhosphorLink weight="fill" {...props} />,
      href: MERCHANT_ROUTES.CHECKOUT_LINKS,
    },
  ], [orderCount]);

  const [addChannelModalOpen, setAddChannelModalOpen] = useState(false);
  const [userAddedChannels, setUserAddedChannels] = useState<string[]>(() => {
    try {
      if (!activeStoreId) return [];
      const saved = localStorage.getItem(`user-added-sales-channels-${activeStoreId}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (!activeStoreId) {
      setUserAddedChannels([]);
      return;
    }
    try {
      const saved = localStorage.getItem(`user-added-sales-channels-${activeStoreId}`);
      setUserAddedChannels(saved ? JSON.parse(saved) : []);
    } catch {
      setUserAddedChannels([]);
    }
  }, [activeStoreId]);

  useEffect(() => {
    if (!activeStoreId) return;
    const s = store as any;
    if (s?.settings && Array.isArray(s.settings.installedChannels)) {
      setUserAddedChannels((prev) => {
        const merged = Array.from(new Set([...s.settings.installedChannels, ...prev]));
        try {
          localStorage.setItem(`user-added-sales-channels-${activeStoreId}`, JSON.stringify(merged));
        } catch {}
        return merged;
      });
    }
  }, [store, activeStoreId]);

  const connectedChannelIds = useMemo(() => {
    if (!activeStoreId || !store) return [];
    const s = store as any;
    const dbInstalled: string[] = Array.isArray(s?.settings?.installedChannels)
      ? s.settings.installedChannels
      : userAddedChannels;
    const ids: string[] = Array.from(new Set([...dbInstalled, ...userAddedChannels]));
    if (s?.status === 'ACTIVE' && !ids.includes('online_store')) ids.push('online_store');
    if (s?.googleMerchantId && !ids.includes('google')) ids.push('google');
    if (s?.metaCommerceEnabled && !ids.includes('meta')) ids.push('meta');
    if (s?.whatsappConnected && !ids.includes('whatsapp')) ids.push('whatsapp');
    return ids;
  }, [store, userAddedChannels, activeStoreId]);

  const salesChannels: NavItem[] = useMemo(() => {
    const list: NavItem[] = [];
    if (connectedChannelIds.includes('google')) {
      list.push({ label: 'Google & YouTube', icon: (props: any) => <PhosphorGoogleLogo weight="fill" {...props} />, href: MERCHANT_ROUTES.GOOGLE_COMMERCE });
    }
    if (connectedChannelIds.includes('meta')) {
      list.push({ label: 'Facebook & Instagram', icon: (props: any) => <PhosphorFacebookLogo weight="fill" {...props} />, href: MERCHANT_ROUTES.META_COMMERCE });
    }
    if (connectedChannelIds.includes('whatsapp')) {
      list.push({ label: 'WhatsApp Catalog', icon: (props: any) => <ChatCircle weight="fill" {...props} />, href: MERCHANT_ROUTES.WHATSAPP_CATALOG });
    }
    if (connectedChannelIds.includes('affiliates')) {
      list.push({ label: 'Affiliates', icon: (props: any) => <ShareNetwork weight="fill" {...props} />, href: '/affiliates' });
    }
    if (connectedChannelIds.includes('pos')) {
      list.push({ label: 'Point of Sale (POS)', icon: (props: any) => <CreditCard weight="fill" {...props} />, href: '/pos' });
    }
    if (connectedChannelIds.includes('buy_button')) {
      list.push({ label: 'Buy Button', icon: (props: any) => <Code weight="bold" {...props} />, href: '/buy-button' });
    }
    return list;
  }, [connectedChannelIds]);

  const handleAddSalesChannel = async (channel: SalesChannelOption) => {
    if (!activeStoreId) return;

    const currentPlan = user?.plan || 'BASIC';
    const maxChannels = (SALES_CHANNEL_LIMITS as any)[currentPlan] ?? 1;

    if (channel.id !== 'online_store' && currentPlan === 'BASIC') {
      setAddChannelModalOpen(false);
      useUpgradeStore.getState().open('sales_channel_limit');
      return;
    }

    if (connectedChannelIds.length >= maxChannels && !connectedChannelIds.includes(channel.id)) {
      setAddChannelModalOpen(false);
      useUpgradeStore.getState().open('sales_channel_limit');
      return;
    }

    const next = Array.from(new Set([...connectedChannelIds, channel.id]));
    setUserAddedChannels(next);
    try {
      localStorage.setItem(`user-added-sales-channels-${activeStoreId}`, JSON.stringify(next));
    } catch {}
    setAddChannelModalOpen(false);

    if (activeStoreId) {
      try {
        await apiClient.patch(API_ENDPOINTS.MERCHANT.STORE, {
          settings: {
            ...((store as any)?.settings || {}),
            installedChannels: next,
          },
        });
        queryClient.invalidateQueries({ queryKey: ['merchant-store'] });
      } catch (err) {
        console.error('Failed to save sales channel to database:', err);
      }
    }

    navigate(channel.href);
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [expandedItems, setExpandedItems] = useState<string[]>(['Products', 'Orders']);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(isCollapsed));
  }, [isCollapsed]);

  const toggleExpanded = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter(i => i !== label) : [...prev, label]
    );
  };

  // Auto-expand the current category if one of its children is active
  useEffect(() => {
    const activeCategory = mainNavigation.find(item => 
      item.children?.some(child => location.pathname === child.href || location.pathname.startsWith(`${child.href}/`))
    );
    if (activeCategory) {
      setExpandedItems(prev => prev.includes(activeCategory.label) ? prev : [...prev, activeCategory.label]);
    }
  }, [location.pathname, mainNavigation]);

  const handleLogoutLocal = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href: string) => {
    if (href === MERCHANT_ROUTES.DASHBOARD) {
      return location.pathname === MERCHANT_ROUTES.DASHBOARD;
    }
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const isChildActive = (children?: { href: string }[]) => {
    return children?.some(child => isActive(child.href)) || false;
  };

  const isSettingsPage = location.pathname.startsWith('/settings') || location.pathname.startsWith('/store');

  return (
    <SSEProvider>
      <div className="h-screen bg-white flex overflow-hidden font-sans text-[#202223]">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/40 lg:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
        )}

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        navigation={mainNavigation}
        salesChannels={salesChannels}
        user={user}
        handleLogout={handleLogoutLocal}
        expandedItems={expandedItems}
        toggleExpanded={toggleExpanded}
        isActive={isActive}
        isChildActive={isChildActive}
        storeName={store?.name || (storesResponse || user?.ownedStores || []).find(s => s.id === activeStoreId)?.name}
        store={store}
        hasOnlineStoreChannel={connectedChannelIds.includes('online_store') || store?.status === 'ACTIVE'}
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        ownedStores={storesResponse || user?.ownedStores || []}
        onStoreSwitch={handleStoreSwitch}
        activeStoreId={activeStoreId}
        navigate={navigate}
        location={location}
        onOpenAddChannelModal={() => setAddChannelModalOpen(true)}
      />

      <div className="flex-1 flex flex-col min-w-0 bg-[#F6F6F7] relative h-screen">
        <Header
          setSidebarOpen={setSidebarOpen}
          notificationsOpen={notificationsOpen}
          setNotificationsOpen={setNotificationsOpen}
          unreadCount={unreadCount}
          user={user}
          navigate={navigate}
        />

        <main className={`flex-1 overflow-y-auto custom-scrollbar flex flex-col ${isSettingsPage ? 'p-0' : 'p-4 lg:p-8'}`}>
          <div className={`flex-1 flex flex-col ${isSettingsPage ? '' : 'max-w-[1200px] w-full mx-auto'}`}>
            <Outlet />
          </div>
        </main>
      </div>
      <ToastContainer />
      <UpgradeModal />
      <AddSalesChannelModal
        isOpen={addChannelModalOpen}
        onClose={() => setAddChannelModalOpen(false)}
        connectedChannelIds={connectedChannelIds}
        onAddChannel={handleAddSalesChannel}
      />
      </div>
    </SSEProvider>
  );
}
